import React from 'react'

/**
 * Tool to let the user select a rectangular area over an image.
 * Used for defining an area in a draft's page, this area (or box) can
 * be used to set it as the OCR area for detecting addresses or references.
 * 
 * Usage: <BoxSelector imageSrc='images/postamail-logo-bg3.png' canvasWidth={500} canvasHeight={200} onChanged={(l, t, w, h) => this.handleAddressBoxChange(l, t, w, h)} />
 */
export class BoxSelector extends React.Component {

  constructor(props) {
    super(props)
    this.state = {
      // Width and height in pixels given to the selector. The displayed background image will be resized to this width and heigth.
      canvasWidth: 1,
      canvasHeight: 1,

      // Pixel coordinates where the user has started to draw the rectangle.
      beginX: 0,
      beginY: 0,

      // Pixel coordinates where the user completes drawing the rectangle.
      endX: 0,
      endY: 0,

      // Used to take the initial values from properties, then forget those once the user starts editing.
      init: true,
    }

    // The canvas object used to draw the background image and the foreground rectangle as it is being drawn by the user.
    this.canvasRef = React.createRef()

    // The image object to contain the image source as passed by properties. To be drawn as the background of the selector.
    this.imagePage = new Image()
  }

  componentDidMount() {

    if (this.props) {
      this.imagePage.src = this.props.imageSrc

    if (this.state.init) {
      this.setState({
        canvasWidth: this.props.canvasWidth,
        canvasHeight: this.props.canvasHeight,
        beginX: this.props.left ? this.props.canvasWidth * (this.props.left / 100.0) : 0,
        beginY: this.props.top ? this.props.canvasHeight * (this.props.top / 100.0) : 0,
        endX: this.props.left && this.props.width ? this.props.canvasWidth * (this.props.left + this.props.width) / 100.0 : 0,
        endY: this.props.top && this.props.height ? this.props.canvasHeight * (this.props.top + this.props.height) / 100.0 : 0,
      })
    }
    }
    
    // Let the background image appear in the canvas when ready.
    this.imagePage.onload = () => this.drawBox()
  }

  /**
   * Expected properties are:
   * - imageSrc: to contain the URL of the background image. Probably to be used in the form of a preloaded base64 data image.
   * - canvasWidth: horizontal size in pixels to be given to the selector, the active part of this component.
   * - canvasHeight: vertical size in pixels to be given to the selector, the active part of this component.
   * - left: left coordinate of the initial rectangle, in percentage of canvas width.
   * - top: top coordinate of the initial rectangle, in percentage of canvas height.
   * - width: horizontal size of the initial rectangle, in percentage of canvas width.
   * - height: vertical size of the initial rectangle, in percentage of canvas height.
   */
  componentDidUpdate(prevProps) {
    if (prevProps === this.props) return
    
    this.imagePage.src = this.props.imageSrc

    if (this.state.init) {
      this.setState({
        canvasWidth: this.props.canvasWidth,
        canvasHeight: this.props.canvasHeight,
        beginX: this.props.left ? this.props.canvasWidth * (this.props.left / 100.0) : 0,
        beginY: this.props.top ? this.props.canvasHeight * (this.props.top / 100.0) : 0,
        endX: this.props.left && this.props.width ? this.props.canvasWidth * (this.props.left + this.props.width) / 100.0 : 0,
        endY: this.props.top && this.props.height ? this.props.canvasHeight * (this.props.top + this.props.height) / 100.0 : 0,
      })
    }
  }

  handlePointerDown = (e) => {
    if (e.button !== 0 || e.buttons !== 1) return
    this.setState({
      init: false, // Forget initial values from now on.
      beginX: e.nativeEvent.offsetX,
      beginY: e.nativeEvent.offsetY,
      endX: e.nativeEvent.offsetX,
      endY: e.nativeEvent.offsetY,
    })
  }

  handlePointerMove = (e) => {
    if (e.button !== 0 || e.buttons !== 1) return
    this.setState({
      endX: e.nativeEvent.offsetX,
      endY: e.nativeEvent.offsetY,
    })
    this.drawBox()
  }

  handlePointerUp = (e) => {
    if (e.button !== 0) return
    this.setState({
      endX: e.nativeEvent.offsetX,
      endY: e.nativeEvent.offsetY,
    })
    this.drawBox()

    // Pass the selected area to the container, in percentages.
    var l = 100.0 * this.state.beginX / this.canvasRef.current.width
    var t = 100.0 * this.state.beginY / this.canvasRef.current.height
    var w = 100.0 * (e.nativeEvent.offsetX - this.state.beginX) / this.canvasRef.current.width
    var h = 100.0 * (e.nativeEvent.offsetY - this.state.beginY) / this.canvasRef.current.height

    // Invert coordinates if the user moved from right to left and/or bottom to top, i.e. avoid negative width or height.
    if (w < 0) {
      w = -w;
      l = l - w;
    }
    if (h < 0) {
      h = -h;
      t = t - h;
    }

    // Call back to the container.
    if (this.props.onChanged) this.props.onChanged(l, t, w, h)
  }

  /**
   * Refreshes the content of the selector.
   * The background image is drawn below the current box selection.
   */
  drawBox() {
    const canvas = this.canvasRef.current;

    if (!canvas) return 
    
    const context = canvas.getContext('2d');

    canvas.setAttribute('width', canvas.offsetWidth);
    canvas.setAttribute('height', canvas.offsetHeight);

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);

    // Background image filling the whole area.
    context.drawImage(this.imagePage, 0, 0, canvas.width, canvas.height)

    if (this.state.width !== 0 && this.state.height !== 0) {
      // Current selected rectangle.
      context.strokeStyle = "green";
      context.lineWidth = 2;
      context.strokeRect(this.state.beginX, this.state.beginY, this.state.endX - this.state.beginX, this.state.endY - this.state.beginY);
    }
  }

  render() {
    return (
      <div>
        <canvas ref={this.canvasRef}
          style={{
            width: this.state.canvasWidth + "px",
            height: this.state.canvasHeight + "px",
            background: "rgba(250, 250, 250, 1.0)",
            border: "solid 2px gray"
          }}
          onMouseDown={(e) => this.handlePointerDown(e)}
          onMouseMove={(e) => this.handlePointerMove(e)}
          onMouseUp={(e) => this.handlePointerUp(e)}
        />
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/common/BoxSelector.js